import React from 'react';

/** The drag payload. A bare id — the grid resolves it against its own list. */
const DRAG_TYPE = 'text/plain';

type UsePinnedDragArgs = {
    id: string;
    onReorder: (sourceId: string, targetId: string) => void;
    onMove: (id: string, delta: number) => void;
};

export type UsePinnedDragResult = {
    onDragStart: (event: React.DragEvent) => void;
    onDragOver: (event: React.DragEvent) => void;
    onDrop: (event: React.DragEvent) => void;
    /** Bound to the grab handle, not to the row: see below. */
    onHandleKeyDown: (event: React.KeyboardEvent) => void;
};

/**
 * Reordering one pinned tile, by pointer and by keyboard.
 *
 * Both gestures end in the same splice on the pinned id list, so this is one
 * operation reached two ways rather than two implementations of it.
 *
 * **The keyboard path is not optional.** A drag-only reorder is a reorder a
 * keyboard cannot perform at all, and the alternative — a move-up and a
 * move-down button on every tile — puts three controls on a card that shows one
 * name. `ArrowUp`/`ArrowDown` on the grab handle is the same affordance the
 * pointer uses, operated the other way.
 */
export const usePinnedDrag = ({
    id,
    onReorder,
    onMove,
}: UsePinnedDragArgs): UsePinnedDragResult => {
    const onDragStart = React.useCallback(
        (event: React.DragEvent) => {
            event.dataTransfer.setData(DRAG_TYPE, id);
            event.dataTransfer.effectAllowed = 'move';
        },
        [id],
    );

    const onDragOver = React.useCallback((event: React.DragEvent) => {
        // Without this the drop never fires: the default handling of a
        // dragover is "this is not a drop target".
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = React.useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();
            const sourceId = event.dataTransfer.getData(DRAG_TYPE);
            if (sourceId) {
                onReorder(sourceId, id);
            }
        },
        [id, onReorder],
    );

    const onHandleKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
                return;
            }
            // Preventing default keeps the page still: the arrows would
            // otherwise scroll it out from under the tile being moved.
            event.preventDefault();
            onMove(id, event.key === 'ArrowDown' ? 1 : -1);
        },
        [id, onMove],
    );

    return { onDragStart, onDragOver, onDrop, onHandleKeyDown };
};
